import React, { useState } from 'react';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { company } from '../config/company';
import s from '../styles/legalStyles';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Data-deletion request page. Required for Google Play (users must be able
 * to request deletion of their data from within/outside the app) and for
 * GDPR "right to erasure".
 *
 * Two paths on this page:
 *   1. Info + direct mailto: for users who prefer email.
 *   2. Inline form that POSTs to /api/data-deletion-request so the request
 *      arrives at our leads pipeline (we reuse `POST /api/leads` under the
 *      hood — no new backend model needed).
 */
const DataDeletion = () => {
  const { language } = useLanguage();
  const isNL = language === 'nl';
  const [email, setEmail] = useState('');
  const [details, setDetails] = useState('');
  const [status, setStatus] = useState('idle'); // idle | working | success | error

  const submit = async (e) => {
    e.preventDefault();
    setStatus('working');
    try {
      // Reuse the leads endpoint (which is what the CRM already reads) —
      // we mark the source so the admin can filter data-deletion requests.
      const res = await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: isNL ? 'Verzoek gegevensverwijdering' : 'Data deletion request',
          last_name: '',
          email: email.trim(),
          phone: '',
          message: `[DATA DELETION REQUEST]\n\n${details.trim() || (isNL ? 'Verzoek tot verwijdering van alle persoonsgegevens.' : 'Request to delete all personal data.')}`,
          source: 'data-deletion',
          cta_type: 'data-deletion',
        }),
      });
      if (!res.ok) throw new Error('submit-failed');
      setStatus('success');
      setEmail('');
      setDetails('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div style={s.page}>
      <SEO
        title={isNL ? 'Gegevensverwijdering aanvragen' : 'Data Deletion Request'}
        description={isNL
          ? `Vraag verwijdering aan van uw persoonsgegevens bij ${company.brand}. Wij verwerken uw verzoek binnen 30 dagen conform de AVG.`
          : `Request deletion of your personal data at ${company.brand}. We process your request within 30 days under GDPR.`}
        noindex
      />
      <div style={s.container}>
        <h1 style={s.title}>{isNL ? 'Gegevensverwijdering aanvragen' : 'Request Data Deletion'}</h1>
        <p style={s.lastUpdated}>
          {isNL ? 'Laatst bijgewerkt:' : 'Last updated:'} {company.effectiveDate}
        </p>

        <article style={s.article}>
          {isNL ? (
            <>
              <p>
                Op grond van de AVG/GDPR heeft u het recht om verwijdering van uw persoonsgegevens
                te verzoeken. {company.brand} verwerkt uw verzoek binnen <strong>30 dagen</strong> na ontvangst.
              </p>

              <h2 style={s.h2}>Welke gegevens verwijderen wij?</h2>
              <ul>
                <li>Naam, e-mailadres en telefoonnummer die u via een contactformulier heeft achtergelaten</li>
                <li>Berichtinhoud van aanvragen die u heeft verstuurd</li>
                <li>Opgeslagen zoekopdrachten en pushmelding-abonnementen (indien van toepassing)</li>
                <li>Alle overige aan uw e-mailadres gekoppelde gegevens in onze database</li>
              </ul>

              <h2 style={s.h2}>Wat wordt bewaard?</h2>
              <p>
                Sommige gegevens moeten wij bewaren op grond van wettelijke verplichtingen
                (bijv. fiscale bewaarplicht bij daadwerkelijke aankooptransacties). Deze
                worden geanonimiseerd na afloop van de wettelijke termijn.
              </p>

              <h2 style={s.h2}>Verzoek indienen</h2>
              <p>Vul onderstaand formulier in óf stuur een e-mail naar <a href={`mailto:${company.email}?subject=Verzoek gegevensverwijdering`}>{company.email}</a>.</p>
            </>
          ) : (
            <>
              <p>
                Under GDPR you have the right to request deletion of your personal data.
                {' '}{company.brand} will process your request within <strong>30 days</strong> of receipt.
              </p>

              <h2 style={s.h2}>What data do we delete?</h2>
              <ul>
                <li>Name, email and phone number you submitted via any contact form</li>
                <li>Message contents of inquiries you have sent</li>
                <li>Saved searches and push-notification subscriptions (if any)</li>
                <li>All other data linked to your email address in our database</li>
              </ul>

              <h2 style={s.h2}>What is retained?</h2>
              <p>
                Some data must be retained under statutory obligations (e.g. tax retention
                periods for completed purchase transactions). Such data is anonymised after
                the legal retention period ends.
              </p>

              <h2 style={s.h2}>Submit request</h2>
              <p>Fill in the form below or email <a href={`mailto:${company.email}?subject=Data deletion request`}>{company.email}</a>.</p>
            </>
          )}

          <form onSubmit={submit} style={styles.form} data-testid="data-deletion-form">
            <label style={styles.label}>
              {isNL ? 'Uw e-mailadres (verplicht)' : 'Your email (required)'}
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={isNL ? 'uw@email.nl' : 'you@email.com'}
              style={styles.input}
              disabled={status === 'working' || status === 'success'}
              data-testid="data-deletion-email"
            />
            <p style={styles.helper}>
              {isNL
                ? 'Wij zoeken alle records met dit e-mailadres en verwijderen ze binnen 30 dagen.'
                : 'We find every record tied to this email and delete them within 30 days.'}
            </p>

            <label style={styles.label}>
              {isNL ? 'Toelichting (optioneel)' : 'Additional details (optional)'}
            </label>
            <textarea
              rows={4}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder={isNL ? 'Bijvoorbeeld welke specifieke gegevens u wilt laten verwijderen…' : 'For example which specific data you want deleted…'}
              style={styles.textarea}
              disabled={status === 'working' || status === 'success'}
              data-testid="data-deletion-details"
            />

            {status === 'success' && (
              <div style={styles.success} data-testid="data-deletion-success">
                {isNL
                  ? '✓ Verzoek ontvangen. U ontvangt binnen 30 dagen een bevestiging per e-mail.'
                  : '✓ Request received. You will get a confirmation by email within 30 days.'}
              </div>
            )}
            {status === 'error' && (
              <div style={styles.error} data-testid="data-deletion-error">
                {isNL
                  ? 'Er ging iets mis. Stuur uw verzoek per e-mail als dit blijft gebeuren.'
                  : 'Something went wrong. Please email us your request if this continues.'}
              </div>
            )}

            <button
              type="submit"
              style={styles.submit}
              disabled={status === 'working' || status === 'success'}
              data-testid="data-deletion-submit"
            >
              {status === 'working'
                ? (isNL ? 'Bezig…' : 'Working…')
                : status === 'success'
                  ? (isNL ? 'Verzonden' : 'Sent')
                  : (isNL ? 'Verzoek indienen' : 'Submit request')}
            </button>
          </form>
        </article>
      </div>
    </div>
  );
};

const styles = {
  form: { marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  label: { fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#8C8B88', marginTop: '0.6rem' },
  input: { padding: '0.75rem 0.9rem', border: '1px solid #EAE5DD', fontFamily: 'inherit', fontSize: '0.95rem', color: '#1A1918', backgroundColor: 'white', outline: 'none' },
  textarea: { padding: '0.75rem 0.9rem', border: '1px solid #EAE5DD', fontFamily: 'inherit', fontSize: '0.95rem', color: '#1A1918', backgroundColor: 'white', outline: 'none', resize: 'vertical' },
  helper: { fontSize: '0.75rem', color: '#8C8B88', margin: '0.15rem 0 0.4rem' },
  submit: { marginTop: '1rem', padding: '0.85rem 1.5rem', backgroundColor: '#1A1918', color: '#C5A059', border: 'none', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', alignSelf: 'flex-start', fontFamily: 'inherit' },
  success: { marginTop: '0.75rem', padding: '0.7rem 0.9rem', backgroundColor: '#E5F1E7', color: '#1F5D2E', border: '1px solid #B8D9BF', fontSize: '0.88rem' },
  error: { marginTop: '0.75rem', padding: '0.7rem 0.9rem', backgroundColor: '#F7E5E2', color: '#8A2A1F', border: '1px solid #E9C0B8', fontSize: '0.88rem' },
};

export default DataDeletion;
